import React, { useEffect, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { useQuery } from "@tanstack/react-query";
import {
  BookOpen,
  CalendarRange,
  CheckCircle2,
  LogOut,
  Mail,
  Phone,
  User,
} from "lucide-react";
import {
  extractTeacherRecord,
  formatTeacherValue,
  getTeacherDisplayName,
  getTeacherEmail,
  humanizeTeacherField,
  teacherApi,
} from "../../features/teacher/teacherApi";
import {
  clearTeacherSession,
  updateTeacherProfile,
} from "../../features/teacher/teacherSlice";
import { formatAudAmount } from "../../utils/audCurrency";

const YEAR_OPTIONS = [
  { id: 6, label: "Year 1" },
  { id: 7, label: "Year 2" },
  { id: 8, label: "Year 3" },
  { id: 9, label: "Year 4" },
  { id: 10, label: "Year 5" },
  { id: 11, label: "Year 6" },
  { id: 12, label: "Year 7" },
  { id: 13, label: "Year 8" },
  { id: 1, label: "Year 9" },
  { id: 2, label: "Year 10" },
  { id: 3, label: "Year 11" },
  { id: 4, label: "Year 12" },
  { id: 5, label: "Year 13" },
];

const YEAR_LABEL_LOOKUP = new Map(
  YEAR_OPTIONS.map((option) => [String(option.id), option.label])
);

const profileStyles = `
  @import url('https://fonts.googleapis.com/css2?family=Lato:wght@300;400;700;900&display=swap');

  .teacher-profile-page {
    min-height: 100vh;
    background: #faf5ec;
    color: #1f1a17;
    font-family: "Lato", sans-serif;
    font-size: 1rem;
  }

  .teacher-profile-topbar {
    position: sticky;
    top: 0;
    z-index: 10;
    background: rgba(255, 255, 255, 0.95);
    border-bottom: 1px solid #ebe4d8;
    backdrop-filter: blur(14px);
  }

  .teacher-profile-shell {
    width: min(100%, 78rem);
    margin: 0 auto;
    padding-inline: clamp(0.875rem, 2vw, 1.25rem);
  }

  .teacher-profile-topbar-row {
    min-height: clamp(4rem, 5.5vw, 4.5rem);
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: clamp(1rem, 2vw, 1.5rem);
  }

  .teacher-profile-brand {
    display: flex;
    align-items: center;
    gap: 0.75rem;
  }

  .teacher-profile-brand-badge {
    width: clamp(2.25rem, 2.8vw, 2.45rem);
    height: clamp(2.25rem, 2.8vw, 2.45rem);
    border-radius: 999px;
    display: grid;
    place-items: center;
    background: linear-gradient(135deg, #ff7154 0%, #ff564d 100%);
    color: #ffffff;
    box-shadow: 0 12px 20px rgba(255, 98, 77, 0.2);
  }

  .teacher-profile-brand-title {
    margin: 0;
    color: #ff624d;
    font-size: clamp(1.1rem, 1.7vw, 1.35rem);
    font-weight: 700;
  }

  .teacher-profile-actions {
    display: flex;
    align-items: center;
    gap: clamp(0.75rem, 1.5vw, 1rem);
  }

  .teacher-profile-email-pill {
    display: inline-flex;
    align-items: center;
    gap: 0.625rem;
    min-height: 2.5rem;
    padding-inline: 1rem;
    border: 1px solid #dbe3f2;
    border-radius: 999px;
    background: #ffffff;
    color: #6f6a67;
    font-size: 0.9rem;
    white-space: nowrap;
  }

  .teacher-profile-logout {
    display: inline-flex;
    align-items: center;
    gap: 0.625rem;
    border: 0;
    background: transparent;
    color: #1f1a17;
    font: inherit;
    font-size: 0.94rem;
    cursor: pointer;
  }

  .teacher-profile-tabs {
    display: flex;
    align-items: center;
    gap: clamp(0.625rem, 1.5vw, 0.95rem);
    min-height: 3.35rem;
  }

  .teacher-profile-tab {
    min-height: 3.35rem;
    display: inline-flex;
    align-items: center;
    gap: 0.625rem;
    padding-inline: 0.95rem;
    border: 0;
    border-bottom: 0.1875rem solid transparent;
    background: transparent;
    color: #2f2a28;
    font: inherit;
    font-size: 0.95rem;
    cursor: pointer;
  }

  .teacher-profile-tab-active {
    color: #2453d4;
    border-bottom-color: #2453d4;
    background: rgba(226, 235, 255, 0.78);
    border-top-left-radius: 0.875rem;
    border-top-right-radius: 0.875rem;
  }

  .teacher-profile-content {
    padding: clamp(1.25rem, 2.5vw, 1.65rem) clamp(0.875rem, 1.6vw, 1.25rem)
      clamp(2rem, 4vw, 2.75rem);
  }

  .teacher-profile-status {
    border-radius: 1rem;
    border: 1px solid transparent;
    padding: 0.95rem 1rem;
    margin-bottom: 1rem;
    font-size: 0.92rem;
  }

  .teacher-profile-status-error {
    border-color: #ffd1c7;
    background: #fff3f0;
    color: #b34736;
  }

  .teacher-profile-status-info {
    border-color: #d7e3fb;
    background: #dfe8fb;
    color: #315db7;
  }

  .teacher-profile-overview-card {
    border: 1px solid #dde5ef;
    border-radius: 1.5rem;
    background: #ffffff;
    box-shadow: 0 26px 40px rgba(71, 85, 110, 0.09);
    overflow: hidden;
  }

  .teacher-profile-overview-head {
    display: grid;
    grid-template-columns: auto minmax(0, 1fr) auto;
    gap: 1.25rem;
    align-items: center;
    padding: clamp(1.25rem, 2.4vw, 1.9rem);
    background:
      linear-gradient(135deg, rgba(223, 232, 251, 0.95) 0%, rgba(255, 255, 255, 0.98) 58%),
      linear-gradient(135deg, rgba(255, 239, 200, 0.72) 0%, rgba(255, 255, 255, 0) 100%);
    border-bottom: 1px solid #e6edf8;
  }

  .teacher-profile-avatar {
    width: clamp(5.2rem, 8vw, 6rem);
    height: clamp(5.2rem, 8vw, 6rem);
    border-radius: 1.5rem;
    overflow: hidden;
    display: grid;
    place-items: center;
    background: #1f1a17;
    color: #ffffff;
    font-size: clamp(1.5rem, 2.5vw, 1.9rem);
    font-weight: 800;
    box-shadow: 0 16px 26px rgba(31, 26, 23, 0.18);
  }

  .teacher-profile-avatar img {
    width: 100%;
    height: 100%;
    object-fit: cover;
    display: block;
  }

  .teacher-profile-overview-copy {
    min-width: 0;
  }

  .teacher-profile-overview-eyebrow {
    display: inline-flex;
    align-items: center;
    gap: 0.45rem;
    min-height: 2rem;
    padding: 0.3rem 0.8rem;
    border-radius: 999px;
    background: #ffffff;
    border: 1px solid #dbe4f7;
    color: #2453d4;
    font-size: 0.82rem;
    font-weight: 800;
    letter-spacing: 0.04em;
    text-transform: uppercase;
  }

  .teacher-profile-overview-copy h2 {
    margin: 0.9rem 0 0.4rem;
    font-size: clamp(1.7rem, 2.6vw, 2.2rem);
    line-height: 1.08;
    font-weight: 900;
  }

  .teacher-profile-overview-copy p {
    margin: 0;
    color: #6c6764;
    font-size: 0.98rem;
    line-height: 1.6;
    max-width: 40rem;
  }

  .teacher-profile-inline-meta {
    display: flex;
    flex-wrap: wrap;
    gap: 0.65rem;
    margin-top: 1rem;
  }

  .teacher-profile-inline-chip {
    min-height: 2.3rem;
    padding-inline: 0.9rem;
    display: inline-flex;
    align-items: center;
    gap: 0.5rem;
    border-radius: 999px;
    background: #ffffff;
    border: 1px solid #dfe6f2;
    color: #3d3a39;
    font-size: 0.9rem;
    font-weight: 700;
  }

  .teacher-profile-overview-actions {
    display: flex;
    justify-content: flex-end;
  }

  .teacher-profile-update-button {
    min-height: 3rem;
    padding: 0.8rem 1.2rem;
    border: 0;
    border-radius: 999px;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    gap: 0.6rem;
    background: linear-gradient(135deg, #ff7154 0%, #ff564d 100%);
    box-shadow: 0 14px 24px rgba(255, 98, 77, 0.22);
    color: #ffffff;
    font: inherit;
    font-size: 0.95rem;
    font-weight: 800;
    cursor: pointer;
    white-space: nowrap;
  }

  .teacher-profile-overview-body {
    padding: clamp(1.25rem, 2.4vw, 1.75rem);
  }

  .teacher-profile-highlight-grid {
    display: grid;
    grid-template-columns: repeat(3, minmax(0, 1fr));
    gap: 0.875rem;
    margin-bottom: 1.25rem;
  }

  .teacher-profile-highlight-card {
    min-height: 5.2rem;
    border-radius: 1rem;
    padding: 0.95rem 1rem;
    display: flex;
    flex-direction: column;
    justify-content: center;
    gap: 0.35rem;
  }

  .teacher-profile-highlight-card span {
    font-size: 0.8rem;
    font-weight: 700;
    letter-spacing: 0.04em;
    text-transform: uppercase;
  }

  .teacher-profile-highlight-card strong {
    font-size: 1.05rem;
    font-weight: 800;
  }

  .teacher-profile-highlight-blue {
    background: #dfe8fb;
    color: #315db7;
  }

  .teacher-profile-highlight-yellow {
    background: #ffefc8;
    color: #b48b2f;
  }

  .teacher-profile-highlight-rose {
    background: #f8dde6;
    color: #bf6786;
  }

  .teacher-profile-highlight-green {
    background: #e3f8ea;
    color: #2d8b57;
  }

  .teacher-profile-section-head {
    display: flex;
    align-items: flex-end;
    justify-content: space-between;
    gap: 1rem;
    margin-bottom: 1rem;
  }

  .teacher-profile-section-head h3 {
    margin: 0 0 0.35rem;
    font-size: 1.12rem;
    font-weight: 800;
  }

  .teacher-profile-section-head p {
    margin: 0;
    color: #75706c;
    font-size: 0.92rem;
  }

  .teacher-profile-detail-grid {
    display: grid;
    grid-template-columns: repeat(3, minmax(0, 1fr));
    gap: 0.9rem;
  }

  .teacher-profile-detail-card {
    min-height: 7rem;
    border: 1px solid #dde5ef;
    border-radius: 1rem;
    background: linear-gradient(180deg, #ffffff 0%, #f9fbff 100%);
    padding: 1rem;
    display: flex;
    flex-direction: column;
    gap: 0.6rem;
  }

  .teacher-profile-detail-label {
    color: #75706c;
    font-size: 0.84rem;
    font-weight: 700;
    letter-spacing: 0.04em;
    text-transform: uppercase;
  }

  .teacher-profile-detail-value {
    color: #1f1a17;
    font-size: 0.98rem;
    font-weight: 700;
    line-height: 1.55;
    word-break: break-word;
  }

  @media (max-width: 1100px) {
    .teacher-profile-overview-head {
      grid-template-columns: auto minmax(0, 1fr);
    }

    .teacher-profile-overview-actions {
      grid-column: 1 / -1;
      justify-content: flex-start;
    }

    .teacher-profile-highlight-grid,
    .teacher-profile-detail-grid {
      grid-template-columns: repeat(2, minmax(0, 1fr));
    }
  }

  @media (max-width: 768px) {
    .teacher-profile-topbar-row {
      min-height: auto;
      padding: 1.125rem 0;
      flex-direction: column;
      align-items: flex-start;
    }

    .teacher-profile-actions {
      width: 100%;
      flex-direction: column;
      align-items: stretch;
    }

    .teacher-profile-email-pill,
    .teacher-profile-logout {
      justify-content: center;
    }

    .teacher-profile-tabs {
      overflow-x: auto;
      padding-bottom: 0.5rem;
    }

    .teacher-profile-tab {
      white-space: nowrap;
    }

    .teacher-profile-overview-head,
    .teacher-profile-section-head {
      grid-template-columns: 1fr;
      flex-direction: column;
      align-items: flex-start;
    }

    .teacher-profile-highlight-grid,
    .teacher-profile-detail-grid {
      grid-template-columns: 1fr;
    }

    .teacher-profile-update-button {
      width: 100%;
    }
  }
`;

const toTrimmedString = (value) =>
  value === null || typeof value === "undefined" ? "" : String(value).trim();

const isPlainObject = (value) =>
  Boolean(value) && typeof value === "object" && !Array.isArray(value);

const parseList = (value) => {
  if (Array.isArray(value)) {
    return value.flatMap((item) => parseList(item));
  }

  if (isPlainObject(value)) {
    return Object.values(value).flatMap((item) => parseList(item));
  }

  const normalizedValue = toTrimmedString(value);
  if (!normalizedValue) return [];

  return normalizedValue
    .split(",")
    .map((item) => item.trim())
    .filter(Boolean);
};

const getAvatarLetter = (value = "") =>
  String(value).trim().charAt(0).toUpperCase() || "T";

const getPhotoUrl = (teacher = {}) =>
  [
    teacher?.photoImage,
    teacher?.photo,
    teacher?.profile_photo,
    teacher?.profilePhoto,
    teacher?.image,
    teacher?.avatar,
  ]
    .map((value) => toTrimmedString(value))
    .find(Boolean) || "";

const isVerifiedTeacher = (value) => {
  if (value === true || value === 1) return true;

  const normalizedValue = toTrimmedString(value).toLowerCase();
  return ["1", "true", "verified", "yes"].includes(normalizedValue);
};

const formatYearsValue = (value) => {
  const yearLabels = parseList(value).map((item) => {
    const normalizedItem = toTrimmedString(item);
    if (!normalizedItem) return "";
    if (YEAR_LABEL_LOOKUP.has(normalizedItem)) {
      return YEAR_LABEL_LOOKUP.get(normalizedItem);
    }
    if (/^year\s/i.test(normalizedItem)) {
      return normalizedItem;
    }
    return `Year ${normalizedItem}`;
  });

  return yearLabels.filter(Boolean).join(", ") || "Not provided";
};

const formatListValue = (value) => {
  const list = parseList(value);
  return list.length ? list.join(", ") : "Not provided";
};

const formatPriceValue = (value) => {
  const trimmedValue = toTrimmedString(value);
  if (!trimmedValue) return "Not provided";

  const formattedAmount = formatAudAmount(trimmedValue);
  return formattedAmount === "AUD amount unavailable" ? trimmedValue : formattedAmount;
};

const shouldOmitExtraField = (key, value, hiddenKeys) => {
  const normalizedKey = String(key).toLowerCase();

  if (hiddenKeys.has(key)) return true;
  if (!toTrimmedString(value) && !Array.isArray(value)) return true;
  if (typeof value === "function") return true;
  if (isPlainObject(value)) return true;

  return ["token", "jwt", "password", "secret", "auth"].some((term) =>
    normalizedKey.includes(term)
  );
};

export default function TeacherProfile() {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const teacherSession = useSelector((state) => state.teacher?.teacher);

  const profileQuery = useQuery({
    queryKey: ["teacher-profile-page"],
    queryFn: teacherApi.getProfile,
    retry: false,
  });

  useEffect(() => {
    const teacherRecord = extractTeacherRecord(profileQuery.data);
    if (teacherRecord) {
      dispatch(updateTeacherProfile(teacherRecord));
    }
  }, [dispatch, profileQuery.data]);

  const teacherProfile = useMemo(
    () =>
      extractTeacherRecord(profileQuery.data, teacherSession || {}) ||
      teacherSession ||
      {},
    [profileQuery.data, teacherSession]
  );

  const profilePayload = profileQuery.data?.data || {};
  const teacherName =
    toTrimmedString(
      profilePayload?.name ||
        teacherProfile?.name ||
        getTeacherDisplayName(teacherProfile)
    ) || "Teacher";
  const teacherEmail =
    toTrimmedString(
      profilePayload?.email ||
        teacherProfile?.email ||
        getTeacherEmail(teacherProfile)
    ) || "Teacher account";
  const teacherPhone =
    toTrimmedString(profilePayload?.phone || teacherProfile?.phone) ||
    "Not provided";
  const teacherExperience =
    toTrimmedString(profilePayload?.experience || teacherProfile?.experience) ||
    "Not provided";
  const teacherQualification =
    toTrimmedString(
      profilePayload?.qualifications ||
        profilePayload?.qualification ||
        teacherProfile?.qualifications ||
        teacherProfile?.qualification
    ) || "Not provided";
  const teacherSubjects = formatListValue(
    profilePayload?.subjects || teacherProfile?.subjects
  );
  const teacherSpecializations = formatListValue(
    profilePayload?.specializations ||
      teacherProfile?.specializations ||
      teacherProfile?.teaching_specializations ||
      teacherProfile?.specialization
  );
  const teacherYearsDisplay = formatYearsValue(
    profilePayload?.year || teacherProfile?.year
  );
  const teacherPrice = formatPriceValue(
    profilePayload?.price_per_session || teacherProfile?.price_per_session
  );
  const teacherCertificate =
    toTrimmedString(
      profilePayload?.certificateImage || teacherProfile?.certificateImage
    ) || "";
  const teacherPhotoUrl = getPhotoUrl({
    ...teacherProfile,
    ...profilePayload,
  });
  const verificationLabel = isVerifiedTeacher(
    profilePayload?.isVerified ?? teacherProfile?.isVerified
  )
    ? "Verified"
    : "Active";

  const highlightCards = [
    {
      label: "Experience",
      value: teacherExperience,
      className: "teacher-profile-highlight-blue",
    },
    {
      label: "Years You Teach",
      value: teacherYearsDisplay,
      className: "teacher-profile-highlight-yellow",
    },
    {
      label: "Status",
      value: verificationLabel,
      className: "teacher-profile-highlight-green",
    },
  ];

  const primaryEntries = useMemo(
    () => [
      { key: "name", label: "Full Name", value: teacherName },
      { key: "email", label: "Email", value: teacherEmail },
      { key: "phone", label: "Phone", value: teacherPhone },
      {
        key: "verification",
        label: "Verification",
        value: verificationLabel,
      },
      {
        key: "experience",
        label: "Years of Experience",
        value: teacherExperience,
      },
      {
        key: "years_you_teach",
        label: "Years You Teach",
        value: teacherYearsDisplay,
      },
      {
        key: "qualifications",
        label: "Qualifications",
        value: teacherQualification,
      },
      { key: "subjects", label: "Subjects", value: teacherSubjects },
      {
        key: "specializations",
        label: "Specializations",
        value: teacherSpecializations,
      },
      {
        key: "price_per_session",
        label: "Price Per Session",
        value: teacherPrice,
      },
      ...(teacherCertificate
        ? [
            {
              key: "certificate_image",
              label: "Certificate Image",
              value: teacherCertificate,
            },
          ]
        : []),
    ],
    [
      teacherCertificate,
      teacherEmail,
      teacherExperience,
      teacherName,
      teacherPhone,
      teacherPrice,
      teacherQualification,
      teacherSpecializations,
      teacherSubjects,
      teacherYearsDisplay,
      verificationLabel,
    ]
  );

  const profileEntries = useMemo(() => {
    const hiddenKeys = new Set([
      "name",
      "full_name",
      "fullName",
      "teacher_name",
      "teacherName",
      "email",
      "email_address",
      "mail",
      "phone",
      "experience",
      "rating",
      "year",
      "qualifications",
      "qualification",
      "subjects",
      "specializations",
      "teaching_specializations",
      "specialization",
      "price_per_session",
      "certificateImage",
      "photoImage",
      "photo",
      "profile_photo",
      "profilePhoto",
      "image",
      "avatar",
      "isVerified",
    ]);

    const extraEntries = Object.entries(teacherProfile)
      .filter(([key, value]) => !shouldOmitExtraField(key, value, hiddenKeys))
      .map(([key, value]) => ({
        key,
        label: humanizeTeacherField(key),
        value: formatTeacherValue(value),
      }));

    return [...primaryEntries, ...extraEntries];
  }, [primaryEntries, teacherProfile]);

  const handleLogout = () => {
    dispatch(clearTeacherSession());
    navigate("/teacherlogin", { replace: true });
  };

  const handleUpdateProfile = () => {
    navigate("/teacherprofile/update");
  };

  const profileNotice = profileQuery.isError
    ? profileQuery.error?.message
    : profileQuery.isLoading && !teacherSession
      ? "Loading teacher profile..."
      : "";

  return (
    <>
      <style>{profileStyles}</style>
      <main className="teacher-profile-page">
        <header className="teacher-profile-topbar">
          <div className="teacher-profile-shell">
            <div className="teacher-profile-topbar-row">
              <div className="teacher-profile-brand">
                <div className="teacher-profile-brand-badge" aria-hidden="true">
                  <BookOpen size={20} />
                </div>
                <h1 className="teacher-profile-brand-title">Teacher Dashboard</h1>
              </div>

              <div className="teacher-profile-actions">
                <div className="teacher-profile-email-pill">
                  <Mail size={18} />
                  {teacherEmail}
                </div>
                <button
                  className="teacher-profile-logout"
                  type="button"
                  onClick={handleLogout}
                >
                  <LogOut size={18} />
                  Logout
                </button>
              </div>
            </div>
          </div>
        </header>

        <div className="teacher-profile-shell">
          <nav className="teacher-profile-tabs" aria-label="Teacher tabs">
            <button
              className="teacher-profile-tab"
              type="button"
              onClick={() => navigate("/teacherdashboard")}
            >
              <BookOpen size={20} />
              Bookings
            </button>
            <button
              className="teacher-profile-tab"
              type="button"
              onClick={() => navigate("/teacheravailability")}
            >
              <CalendarRange size={20} />
              Availability
            </button>
            <button
              className="teacher-profile-tab teacher-profile-tab-active"
              type="button"
            >
              <User size={20} />
              Profile
            </button>
          </nav>
        </div>

        <div className="teacher-profile-shell teacher-profile-content">
          {profileNotice ? (
            <div
              className={`teacher-profile-status ${
                profileQuery.isError
                  ? "teacher-profile-status-error"
                  : "teacher-profile-status-info"
              }`}
            >
              {profileNotice}
            </div>
          ) : null}

          <section className="teacher-profile-overview-card">
            <div className="teacher-profile-overview-head">
              <div className="teacher-profile-avatar" aria-hidden="true">
                {teacherPhotoUrl ? (
                  <img src={teacherPhotoUrl} alt={`${teacherName} profile`} />
                ) : (
                  getAvatarLetter(teacherName)
                )}
              </div>

              <div className="teacher-profile-overview-copy">
                <span className="teacher-profile-overview-eyebrow">
                  <User size={14} />
                  Teacher Profile
                </span>
                <h2>{teacherName}</h2>
                <p>
                  This page now keeps your teacher details in one main location
                  while using the same dashboard colors and surfaces.
                </p>

                <div className="teacher-profile-inline-meta">
                  <span className="teacher-profile-inline-chip">
                    <Mail size={16} color="#2453d4" />
                    {teacherEmail}
                  </span>
                  <span className="teacher-profile-inline-chip">
                    <Phone size={16} color="#2453d4" />
                    {teacherPhone}
                  </span>
                  <span className="teacher-profile-inline-chip">
                    <CheckCircle2 size={16} color="#2453d4" />
                    {verificationLabel}
                  </span>
                </div>
              </div>

              <div className="teacher-profile-overview-actions">
                <button
                  className="teacher-profile-update-button"
                  type="button"
                  onClick={handleUpdateProfile}
                >
                  Update Profile
                </button>
              </div>
            </div>

            <div className="teacher-profile-overview-body">
              <div className="teacher-profile-highlight-grid">
                {highlightCards.map((card) => (
                  <div
                    key={card.label}
                    className={`teacher-profile-highlight-card ${card.className}`}
                  >
                    <span>{card.label}</span>
                    <strong>{card.value}</strong>
                  </div>
                ))}
              </div>

              <div className="teacher-profile-section-head">
                <div>
                  <h3>Profile Overview</h3>
                  <p>All available teacher information is grouped here.</p>
                </div>
              </div>

              <div className="teacher-profile-detail-grid">
                {profileEntries.map((entry) => (
                  <article
                    key={entry.key}
                    className="teacher-profile-detail-card"
                  >
                    <span className="teacher-profile-detail-label">
                      {entry.label}
                    </span>
                    <span className="teacher-profile-detail-value">
                      {entry.value}
                    </span>
                  </article>
                ))}
              </div>
            </div>
          </section>
        </div>
      </main>
    </>
  );
}
